import type { TemplateTaskConfigCatalog } from '@techgitter/contracts';
import type { TemplateTaskConfigOverride } from '../types/taskList';

const API_BASE_URL = '/api';

/**
 * Fetch the template-task-configs catalog. When `customerId` is supplied
 * the API merges that customer's active task-config overrides in
 * (replace/append), mirroring `getTicketTypeTemplates`.
 */
export async function getTemplateTaskConfigs(
  signalOrOptions?: AbortSignal | { customerId?: string; signal?: AbortSignal },
): Promise<TemplateTaskConfigCatalog> {
  const opts =
    signalOrOptions instanceof AbortSignal
      ? { signal: signalOrOptions }
      : signalOrOptions ?? {};
  const params = new URLSearchParams();
  if ('customerId' in opts && opts.customerId) {
    params.set('customerId', opts.customerId);
  }
  const qs = params.toString();
  const url = `${API_BASE_URL}/template-task-configs${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch template task configs: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
}

export async function putTemplateTaskConfigs(
  catalog: TemplateTaskConfigCatalog,
  signal?: AbortSignal
): Promise<TemplateTaskConfigCatalog> {
  const response = await fetch(`${API_BASE_URL}/template-task-configs`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(catalog),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to save template task configs: ${response.status}`,
    }));
    throw new Error(
      errorData.error || `Failed to save template task configs: ${response.status}`
    );
  }

  return response.json();
}

export async function clearTemplateTaskConfigsCache(
  signal?: AbortSignal
): Promise<{ cleared: boolean }> {
  const response = await fetch(`${API_BASE_URL}/template-task-configs/cache/clear`, {
    method: 'POST',
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to clear template task configs cache: ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Per-customer task-config overrides
// ---------------------------------------------------------------------------

/** A non-null taskConfig — required on create, optional (nullable) on patch. */
type TaskConfigValue = NonNullable<TemplateTaskConfigOverride['taskConfig']>;

function _overridesUrl(customerId: string, overrideId?: string): string {
  const base = `${API_BASE_URL}/customers/${encodeURIComponent(customerId)}/template-task-config-overrides`;
  return overrideId ? `${base}/${encodeURIComponent(overrideId)}` : base;
}

export async function listTemplateTaskConfigOverrides(
  customerId: string,
  signal?: AbortSignal,
): Promise<TemplateTaskConfigOverride[]> {
  const response = await fetch(_overridesUrl(customerId), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'list task-config overrides');
  }
  const body = await response.json();
  return Array.isArray(body.items) ? body.items : [];
}

export async function createTemplateTaskConfigOverride(
  customerId: string,
  payload: { ticketTypeId: string; taskConfig: TaskConfigValue },
  signal?: AbortSignal,
): Promise<TemplateTaskConfigOverride> {
  const response = await fetch(_overridesUrl(customerId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'create task-config override');
  }
  return response.json();
}

export async function updateTemplateTaskConfigOverride(
  customerId: string,
  overrideId: string,
  patch: { taskConfig: TemplateTaskConfigOverride['taskConfig'] },
  signal?: AbortSignal,
): Promise<TemplateTaskConfigOverride> {
  const response = await fetch(_overridesUrl(customerId, overrideId), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(patch),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'update task-config override');
  }
  return response.json();
}

export async function deleteTemplateTaskConfigOverride(
  customerId: string,
  overrideId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(_overridesUrl(customerId, overrideId), {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'delete task-config override');
  }
}

async function _toError(response: Response, action: string): Promise<Error> {
  let message = `Failed to ${action}: ${response.status}`;
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') {
      message = body.error;
    }
  } catch {
    // ignore
  }
  return new Error(message);
}
